import React, { useEffect, useState, useMemo } from 'react';
import { FaArrowRight, FaArrowUpRightFromSquare } from "react-icons/fa6";
import { FaTwitch } from "react-icons/fa";
import jizon from '../assets/jizon2.png';
import StreamEmbed from '../../components/StreamEmbed';
import ClipsList from '../../components/ClipsList';
import PartnersList from '../../components/PartnersList';
import SocialLinks from '../../components/SocialLinks';
import { SkeletonStats } from '../../components/Skeleton/Skeleton';
import { useBetsData } from '../../utils/fetchBetsData';
import { computeBetStats, formatMoney } from '../../utils/betStats';
import { useTwitchStatus } from '../../context/TwitchStatusContext';
import './Home.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const CHANNEL = 'jizonn';
const TWITCH_URL = `https://twitch.tv/${CHANNEL}`;

function SectionHead({ title, sub, action }) {
  return (
    <div className="section-head">
      <div>
        <h2 className="section-title">{title}</h2>
        {sub && <p className="section-sub">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

function Home({ onNavigate }) {
  const { betsData, loading: betsLoading } = useBetsData();
  const { isLive, viewerCount, category, uptime } = useTwitchStatus();
  const [partners, setPartners] = useState([]);

  const stats = useMemo(() => computeBetStats(betsData), [betsData]);

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await fetch(`${API_URL}/partners/`);
        const data = await response.json();
        setPartners(data.results || data);
      } catch (error) {
        console.error('Erro ao carregar parceiros:', error);
      }
    };
    fetchPartners();
  }, []);

  const goTo = (page) => () => onNavigate?.(page);

  const kpis = [
    {
      label: 'Profit total',
      value: formatMoney(stats.profit, { sign: true }),
      tone: stats.profit >= 0 ? 'pos' : 'neg',
    },
    { label: 'Win rate', value: `${stats.winRate.toFixed(1)}%` },
    { label: 'Apostas registadas', value: stats.totalBets },
    { label: 'Pendentes', value: stats.pendingBets, tone: stats.pendingBets > 0 ? 'warn' : undefined },
  ];

  return (
    <>
      {stats.pendingBets > 0 && (
        <button onClick={goTo('bets')} className="floating-bets-badge">
          <span className="bets-badge-count">{stats.pendingBets}</span>
          <span className="bets-badge-text">{stats.pendingBets === 1 ? 'bet pendente' : 'bets pendentes'}</span>
        </button>
      )}

      <main className="page-container home-page" id="top">
        <section className="home-hero">
          <div className="home-intro">
            <div className="home-identity">
              <span className={`home-avatar-wrap ${isLive ? 'is-live' : ''}`}>
                <img className="home-avatar" src={jizon} alt="" decoding="async" width="52" height="52" />
              </span>
              <div className={`home-status ${isLive ? 'is-live' : ''}`} role="status" aria-live="polite">
                <span className="home-status-state">
                  <span className="home-status-dot" />
                  {isLive === null ? 'A verificar…' : isLive ? 'Em direto agora' : 'Offline'}
                </span>
                {isLive && (
                  <span className="home-status-meta">
                    {[category, viewerCount != null ? `${viewerCount} a ver` : null, uptime].filter(Boolean).join(' · ')}
                  </span>
                )}
                {isLive === false && <span className="home-status-meta">twitch.tv/{CHANNEL}</span>}
              </div>
            </div>

            <h1 className="home-name">jizoNN</h1>
            <p className="home-tagline">Streamer • Semi Pro • Apostador Profissional • Amigo do Alvini</p>

            <div className="home-actions">
              <a className="btn btn-primary btn-lg" href={TWITCH_URL} target="_blank" rel="noopener noreferrer">
                <FaTwitch />
                {isLive ? 'Ver em direto' : 'Seguir na Twitch'}
              </a>
              <button className="btn btn-lg" onClick={goTo('bets')}>
                Ver apostas <FaArrowRight />
              </button>
            </div>

            <div className="home-socials">
              <SocialLinks />
            </div>
          </div>

          <div className="home-player">
            <StreamEmbed channel={CHANNEL} />
          </div>
        </section>

        <section className="home-section">
          <SectionHead
            title="Resumo das apostas"
            sub="Todas as apostas registadas até hoje."
            action={<button className="section-link" onClick={goTo('bets')}>Ver todas <FaArrowRight /></button>}
          />
          {betsLoading ? (
            <SkeletonStats />
          ) : (
            <div className="home-kpis">
              {kpis.map(k => (
                <button key={k.label} className={`home-kpi ${k.tone ? `tone-${k.tone}` : ''}`} onClick={goTo('bets')}>
                  <span className="home-kpi-label">{k.label}</span>
                  <span className="home-kpi-value num">{k.value}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        {partners.length > 0 && (
          <section id="partners" className="home-section">
            <SectionHead
              title="Parcerias"
              sub="Códigos e bónus exclusivos."
              action={<button className="section-link" onClick={goTo('parcerias')}>Ver parcerias <FaArrowRight /></button>}
            />
            <PartnersList partners={partners} />
          </section>
        )}

        <section id="clips" className="home-section">
          <SectionHead
            title="Top Clips"
            sub="Os momentos mais vistos do canal."
            action={
              <a className="section-link" href={`${TWITCH_URL}/clips`} target="_blank" rel="noopener noreferrer">
                Ver todos <FaArrowUpRightFromSquare />
              </a>
            }
          />
          <ClipsList />
        </section>
      </main>
    </>
  );
}

export default Home;
